import React, { useState, useRef, useEffect } from "react";
import { Plus, MoreVertical, Edit2, Trash2, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Sidebar({
  isOpen = true,
  sessions = [],
  currentSession,
  editingSession,
  onToggle,
  onNewChat,
  onSelectSession,
  onEditSession,
  onUpdateSession,
  onDeleteSession,
}) {
  const navigate = useNavigate();

  const [editingName, setEditingName] = useState("");
  const [openMenu, setOpenMenu] = useState(null);
  const menuRef = useRef(null);
  const editInputRef = useRef(null);

  const safeSessions = Array.isArray(sessions) ? sessions : [];

  useEffect(() => {
    if (editingSession) {
      const session = safeSessions.find((s) => s.id === editingSession);
      setEditingName(session?.session_name || "");
      setTimeout(() => editInputRef.current?.focus(), 0);
    }
  }, [editingSession, safeSessions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmitEdit = async (sessionId) => {
    if (!editingName.trim()) return;
    await onUpdateSession(sessionId, editingName);
    onEditSession(null);
    setEditingName("");
  };

  const handleCancelEdit = () => {
    onEditSession(null);
    setEditingName("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return "오늘";
    if (days === 1) return "어제";
    if (days < 7) return `${days}일 전`;
    if (days < 30) return `${Math.floor(days / 7)}주 전`;
    if (days < 365) return `${Math.floor(days / 30)}개월 전`;
    return `${Math.floor(days / 365)}년 전`;
  };

  const groupSessionsByDate = () => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: [],
    };
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    safeSessions.forEach((session) => {
      if (!session) return;
      const sessionDate = new Date(session.updated_at);
      if (sessionDate >= today) groups.today.push(session);
      else if (sessionDate >= yesterday) groups.yesterday.push(session);
      else if (sessionDate >= weekAgo) groups.thisWeek.push(session);
      else if (sessionDate >= monthAgo) groups.thisMonth.push(session);
      else groups.older.push(session);
    });
    return groups;
  };

  const sessionGroups = groupSessionsByDate();

  const renderSessionGroup = (title, sessionList) => {
    if (!Array.isArray(sessionList) || sessionList.length === 0) return null;
    return (
      <div key={title} className="mb-3">
        {/* 버튼들과 같은 좌우 라인(px-4 컨테이너)에서 시작 */}
        <div className="py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
          {title}
        </div>

        <div className="space-y-1.5">
          {sessionList.map((session) => {
            if (!session) return null;
            const isActive = currentSession?.id === session.id;
            return (
              <div
                key={session.id}
                className={`group relative rounded-lg transition-all cursor-pointer ${
                  isActive ? "bg-gray-100" : "hover:bg-gray-50"
                }`}
              >
                {editingSession === session.id ? (
                  <div className="flex items-center p-2.5 gap-2">
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmitEdit(session.id);
                        if (e.key === "Escape") handleCancelEdit();
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                      placeholder="세션 이름"
                    />
                    <button
                      onClick={() => handleSubmitEdit(session.id)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => onSelectSession(session)}
                    className="flex items-center justify-between p-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {session.session_name || "새 대화"}
                      </div>
                      {/* 줄간격 통일 위해 날짜 라벨 제거
                      <div className="text-xs text-gray-500 mt-0.5">
                        {formatDate(session.updated_at)}
                      </div> */}
                    </div>

                    <div
                      className="relative"
                      ref={openMenu === session.id ? menuRef : null}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenu(
                            openMenu === session.id ? null : session.id
                          );
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 rounded transition-opacity"
                      >
                        <MoreVertical className="w-4 h-4 text-gray-600" />
                      </button>

                      {openMenu === session.id && (
                        <div className="absolute right-0 mt-1.5 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditSession(session.id);
                              setOpenMenu(null);
                            }}
                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            이름 변경
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteSession(session);
                              setOpenMenu(null);
                            }}
                            className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            삭제
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`${
        isOpen ? "w-64" : "w-0"
      } transition-all duration-300 bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden`}
    >
      {/* 헤더: 경계선 제거, 버튼과 같은 좌우(px-4) */}
      <div className="px-4 py-4">
        <button
          onClick={onNewChat}
          className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-300 transition-colors"
        >
          <Plus className="w-4 h-4" />새 채팅
        </button>
      </div>

      {/* 세션 목록: 버튼/푸터와 동일한 px-4 */}
      <div className="flex-1 overflow-y-auto py-2 px-4">
        {safeSessions.length === 0 ? (
          <div className="text-gray-500 text-sm text-center py-8">
            채팅 기록이 없습니다
          </div>
        ) : (
          <>
            {renderSessionGroup("오늘", sessionGroups.today)}
            {renderSessionGroup("어제", sessionGroups.yesterday)}
            {renderSessionGroup("이번 주", sessionGroups.thisWeek)}
            {renderSessionGroup("이번 달", sessionGroups.thisMonth)}
            {renderSessionGroup("이전", sessionGroups.older)}
          </>
        )}
      </div>

      {/* 푸터: 경계선 제거, 버튼과 같은 좌우(px-4) */}
      <div className="px-4 py-4">
        <button
          onClick={() => navigate("/app")}
          className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-300 transition-colors"
        >
          대시보드 돌아가기
        </button>
      </div>
    </div>
  );
}
